/**
 * OpenAPI Generator
 * Convert API Contract to OpenAPI 3.0 specification
 */

import type {
  APIContract,
  Endpoint,
  SchemaDefinition,
  SchemaComponent,
  SecurityScheme,
  Parameter,
  RequestBody,
  Response,
} from './types';

// OpenAPI types
interface OpenAPISpec {
  openapi: string;
  info: {
    title: string;
    version: string;
    description?: string;
    termsOfService?: string;
    contact?: {
      name?: string;
      email?: string;
      url?: string;
    };
    license?: {
      name: string;
      url?: string;
    };
  };
  servers?: Array<{
    url: string;
    description?: string;
    variables?: Record<string, {
      default: string;
      description?: string;
      enum?: string[];
    }>;
  }>;
  paths: Record<string, Record<string, unknown>>;
  components?: {
    schemas?: Record<string, unknown>;
    securitySchemes?: Record<string, unknown>;
  };
  tags?: Array<{ name: string; description?: string }>;
  security?: Array<Record<string, string[]>>;
}

/**
 * Convert internal schema to OpenAPI schema
 */
function convertSchema(schema: SchemaDefinition): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (schema.$ref) {
    return { $ref: `#/components/schemas/${schema.$ref}` };
  }

  result.type = schema.type;

  if (schema.format) result.format = schema.format;
  if (schema.description) result.description = schema.description;
  if (schema.nullable) result.nullable = schema.nullable;
  if (schema.default !== undefined) result.default = schema.default;
  if (schema.enum) result.enum = schema.enum;
  if (schema.minimum !== undefined) result.minimum = schema.minimum;
  if (schema.maximum !== undefined) result.maximum = schema.maximum;
  if (schema.minLength !== undefined) result.minLength = schema.minLength;
  if (schema.maxLength !== undefined) result.maxLength = schema.maxLength;
  if (schema.pattern) result.pattern = schema.pattern;
  if (schema.example !== undefined) result.example = schema.example;

  // Array items
  if (schema.type === 'array' && schema.items) {
    result.items = convertSchema(schema.items);
  }

  // Object properties
  if (schema.type === 'object' && schema.properties) {
    result.properties = Object.entries(schema.properties).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: convertSchema(value),
      }),
      {}
    );

    // Required fields
    const requiredFields = Object.entries(schema.properties)
      .filter(([, value]) => value.required)
      .map(([key]) => key);
    if (requiredFields.length > 0) {
      result.required = requiredFields;
    }

    if (schema.additionalProperties !== undefined) {
      result.additionalProperties =
        typeof schema.additionalProperties === 'boolean'
          ? schema.additionalProperties
          : convertSchema(schema.additionalProperties);
    }
  }

  return result;
}

/**
 * Convert internal parameter to OpenAPI parameter
 */
function convertParameter(param: Parameter): Record<string, unknown> {
  const result: Record<string, unknown> = {
    name: param.name,
    in: param.in,
    required: param.required,
    schema: convertSchema(param.schema),
  };

  if (param.description) result.description = param.description;
  if (param.deprecated) result.deprecated = param.deprecated;
  if (param.example !== undefined) result.example = param.example;

  return result;
}

/**
 * Convert internal request body to OpenAPI request body
 */
function convertRequestBody(body: RequestBody): Record<string, unknown> {
  const result: Record<string, unknown> = {
    required: body.required,
    content: Object.entries(body.content).reduce(
      (acc, [mediaType, content]) => ({
        ...acc,
        [mediaType]: {
          schema: convertSchema(content.schema),
          ...(content.example !== undefined && { example: content.example }),
          ...(content.examples && {
            examples: Object.entries(content.examples).reduce(
              (exAcc, [name, ex]) => ({
                ...exAcc,
                [name]: {
                  ...(ex.summary && { summary: ex.summary }),
                  ...(ex.description && { description: ex.description }),
                  value: ex.value,
                },
              }),
              {}
            ),
          }),
        },
      }),
      {}
    ),
  };

  if (body.description) result.description = body.description;

  return result;
}

/**
 * Convert internal response to OpenAPI response
 */
function convertResponse(response: Response): Record<string, unknown> {
  const result: Record<string, unknown> = {
    description: response.description,
  };

  if (response.headers) {
    result.headers = Object.entries(response.headers).reduce(
      (acc, [name, header]) => ({
        ...acc,
        [name]: {
          ...(header.description && { description: header.description }),
          ...(header.required !== undefined && { required: header.required }),
          schema: convertSchema(header.schema),
        },
      }),
      {}
    );
  }

  if (response.content) {
    result.content = Object.entries(response.content).reduce(
      (acc, [mediaType, content]) => ({
        ...acc,
        [mediaType]: {
          schema: convertSchema(content.schema),
          ...(content.example !== undefined && { example: content.example }),
        },
      }),
      {}
    );
  }

  return result;
}

/**
 * Convert internal security scheme to OpenAPI security scheme
 */
function convertSecurityScheme(scheme: SecurityScheme): Record<string, unknown> {
  const result: Record<string, unknown> = {
    type: scheme.type,
  };

  if (scheme.description) result.description = scheme.description;

  switch (scheme.type) {
    case 'apiKey':
      result.name = scheme.apiKeyName;
      result.in = scheme.apiKeyIn;
      break;
    case 'http':
      result.scheme = scheme.httpScheme;
      if (scheme.bearerFormat) result.bearerFormat = scheme.bearerFormat;
      break;
    case 'oauth2':
      if (scheme.oauth2Flows) {
        result.flows = {};
        const flows = result.flows as Record<string, unknown>;

        if (scheme.oauth2Flows.implicit) {
          flows.implicit = {
            authorizationUrl: scheme.oauth2Flows.implicit.authorizationUrl,
            scopes: scheme.oauth2Flows.implicit.scopes,
            ...(scheme.oauth2Flows.implicit.refreshUrl && {
              refreshUrl: scheme.oauth2Flows.implicit.refreshUrl,
            }),
          };
        }
        if (scheme.oauth2Flows.password) {
          flows.password = {
            tokenUrl: scheme.oauth2Flows.password.tokenUrl,
            scopes: scheme.oauth2Flows.password.scopes,
            ...(scheme.oauth2Flows.password.refreshUrl && {
              refreshUrl: scheme.oauth2Flows.password.refreshUrl,
            }),
          };
        }
        if (scheme.oauth2Flows.clientCredentials) {
          flows.clientCredentials = {
            tokenUrl: scheme.oauth2Flows.clientCredentials.tokenUrl,
            scopes: scheme.oauth2Flows.clientCredentials.scopes,
            ...(scheme.oauth2Flows.clientCredentials.refreshUrl && {
              refreshUrl: scheme.oauth2Flows.clientCredentials.refreshUrl,
            }),
          };
        }
        if (scheme.oauth2Flows.authorizationCode) {
          flows.authorizationCode = {
            authorizationUrl: scheme.oauth2Flows.authorizationCode.authorizationUrl,
            tokenUrl: scheme.oauth2Flows.authorizationCode.tokenUrl,
            scopes: scheme.oauth2Flows.authorizationCode.scopes,
            ...(scheme.oauth2Flows.authorizationCode.refreshUrl && {
              refreshUrl: scheme.oauth2Flows.authorizationCode.refreshUrl,
            }),
          };
        }
      }
      break;
    case 'openIdConnect':
      result.openIdConnectUrl = scheme.openIdConnectUrl;
      break;
  }

  return result;
}

/**
 * Convert endpoint to OpenAPI operation
 */
function convertEndpoint(endpoint: Endpoint): Record<string, unknown> {
  const operation: Record<string, unknown> = {};

  if (endpoint.operationId) operation.operationId = endpoint.operationId;
  if (endpoint.summary) operation.summary = endpoint.summary;
  if (endpoint.description) operation.description = endpoint.description;
  if (endpoint.tags && endpoint.tags.length > 0) operation.tags = endpoint.tags;
  if (endpoint.deprecated) operation.deprecated = endpoint.deprecated;

  // Parameters
  if (endpoint.parameters.length > 0) {
    operation.parameters = endpoint.parameters.map(convertParameter);
  }

  // Request body
  if (endpoint.requestBody) {
    operation.requestBody = convertRequestBody(endpoint.requestBody);
  }

  // Responses
  if (endpoint.responses.length > 0) {
    operation.responses = endpoint.responses.reduce(
      (acc, response) => ({
        ...acc,
        [response.statusCode]: convertResponse(response),
      }),
      {}
    );
  } else {
    // Default response
    operation.responses = {
      '200': { description: 'Successful response' },
    };
  }

  // Security
  if (endpoint.security && endpoint.security.length > 0) {
    operation.security = endpoint.security.map((req) => ({
      [req.schemeName]: req.scopes || [],
    }));
  }

  return operation;
}

/**
 * Generate OpenAPI specification from contract
 */
export function generateOpenAPI(contract: APIContract): OpenAPISpec {
  const spec: OpenAPISpec = {
    openapi: '3.0.3',
    info: {
      title: contract.name,
      version: contract.version,
    },
    paths: {},
  };

  // Info
  if (contract.description) spec.info.description = contract.description;
  if (contract.termsOfService) spec.info.termsOfService = contract.termsOfService;
  if (contract.contact) spec.info.contact = contract.contact;
  if (contract.license) spec.info.license = contract.license;

  // Servers
  if (contract.servers.length > 0) {
    spec.servers = contract.servers.map((server) => ({
      url: server.url,
      ...(server.description && { description: server.description }),
      ...(server.variables && { variables: server.variables }),
    }));
  }

  // Tags
  if (contract.tags && contract.tags.length > 0) {
    spec.tags = contract.tags.map((tag) => ({
      name: tag.name,
      ...(tag.description && { description: tag.description }),
    }));
  }

  // Paths
  const pathGroups = contract.endpoints.reduce<Record<string, Endpoint[]>>(
    (acc, endpoint) => {
      if (!acc[endpoint.path]) acc[endpoint.path] = [];
      acc[endpoint.path].push(endpoint);
      return acc;
    },
    {}
  );

  for (const [path, endpoints] of Object.entries(pathGroups)) {
    spec.paths[path] = endpoints.reduce(
      (acc, endpoint) => ({
        ...acc,
        [endpoint.method.toLowerCase()]: convertEndpoint(endpoint),
      }),
      {}
    );
  }

  // Components
  if (contract.schemas.length > 0 || contract.securitySchemes.length > 0) {
    spec.components = {};

    // Schemas
    if (contract.schemas.length > 0) {
      spec.components.schemas = contract.schemas.reduce(
        (acc, schema) => ({
          ...acc,
          [schema.name]: {
            ...convertSchema(schema.schema),
            ...(schema.description && { description: schema.description }),
          },
        }),
        {}
      );
    }

    // Security schemes
    if (contract.securitySchemes.length > 0) {
      spec.components.securitySchemes = contract.securitySchemes.reduce(
        (acc, scheme) => ({
          ...acc,
          [scheme.name]: convertSecurityScheme(scheme),
        }),
        {}
      );
    }
  }

  return spec;
}

/**
 * Export to JSON string
 */
export function exportToJSON(contract: APIContract): string {
  const spec = generateOpenAPI(contract);
  return JSON.stringify(spec, null, 2);
}

/**
 * Export to YAML string
 */
export function exportToYAML(contract: APIContract): string {
  const spec = generateOpenAPI(contract);
  return jsonToYaml(spec);
}

/**
 * Simple JSON to YAML converter
 */
function jsonToYaml(obj: unknown, indent: number = 0): string {
  const spaces = '  '.repeat(indent);

  if (obj === null || obj === undefined) {
    return 'null';
  }

  if (typeof obj === 'boolean' || typeof obj === 'number') {
    return String(obj);
  }

  if (typeof obj === 'string') {
    // Check if string needs quoting
    if (
      obj === '' ||
      obj.includes(':') ||
      obj.includes('#') ||
      obj.includes('\n') ||
      obj.startsWith(' ') ||
      obj.endsWith(' ') ||
      /^[0-9]/.test(obj) ||
      ['true', 'false', 'null', 'yes', 'no'].includes(obj.toLowerCase())
    ) {
      return `"${obj.replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    return obj
      .map((item) => {
        const value = jsonToYaml(item, indent + 1);
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          const lines = value.split('\n');
          return `${spaces}- ${lines[0]}\n${lines.slice(1).map(l => `${spaces}  ${l}`).join('\n')}`.trim();
        }
        return `${spaces}- ${value}`;
      })
      .join('\n');
  }

  if (typeof obj === 'object') {
    const entries = Object.entries(obj);
    if (entries.length === 0) return '{}';
    return entries
      .map(([key, value]) => {
        const yamlValue = jsonToYaml(value, indent + 1);
        if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) {
          return `${spaces}${key}:\n${yamlValue}`;
        }
        return `${spaces}${key}: ${yamlValue}`;
      })
      .join('\n');
  }

  return String(obj);
}

/**
 * Generate Markdown documentation
 */
export function generateMarkdown(contract: APIContract): string {
  let md = `# ${contract.name}\n\n`;

  if (contract.description) {
    md += `${contract.description}\n\n`;
  }

  md += `**Version:** ${contract.version}\n\n`;

  // Servers
  if (contract.servers.length > 0) {
    md += `## Servers\n\n`;
    for (const server of contract.servers) {
      md += `- \`${server.url}\``;
      if (server.description) md += ` - ${server.description}`;
      md += '\n';
    }
    md += '\n';
  }

  // Authentication
  if (contract.securitySchemes.length > 0) {
    md += `## Authentication\n\n`;
    for (const scheme of contract.securitySchemes) {
      md += `### ${scheme.name}\n\n`;
      md += `**Type:** ${scheme.type}\n\n`;
      if (scheme.description) md += `${scheme.description}\n\n`;
    }
  }

  // Endpoints grouped by tag
  const endpointsByTag = new Map<string, Endpoint[]>();
  const untaggedEndpoints: Endpoint[] = [];

  for (const endpoint of contract.endpoints) {
    if (endpoint.tags && endpoint.tags.length > 0) {
      for (const tag of endpoint.tags) {
        if (!endpointsByTag.has(tag)) endpointsByTag.set(tag, []);
        endpointsByTag.get(tag)!.push(endpoint);
      }
    } else {
      untaggedEndpoints.push(endpoint);
    }
  }

  // Render endpoints
  const renderEndpoint = (endpoint: Endpoint) => {
    md += `### ${endpoint.method} ${endpoint.path}\n\n`;
    if (endpoint.summary) md += `${endpoint.summary}\n\n`;
    if (endpoint.description) md += `${endpoint.description}\n\n`;
    if (endpoint.deprecated) md += `> **Deprecated**\n\n`;

    // Parameters
    if (endpoint.parameters.length > 0) {
      md += `**Parameters:**\n\n`;
      md += `| Name | In | Type | Required | Description |\n`;
      md += `|------|-----|------|----------|-------------|\n`;
      for (const param of endpoint.parameters) {
        md += `| ${param.name} | ${param.in} | ${param.schema.type} | ${param.required ? 'Yes' : 'No'} | ${param.description || '-'} |\n`;
      }
      md += '\n';
    }

    // Request body
    if (endpoint.requestBody) {
      md += `**Request Body:**\n\n`;
      md += `Required: ${endpoint.requestBody.required ? 'Yes' : 'No'}\n\n`;
      for (const [mediaType, content] of Object.entries(endpoint.requestBody.content)) {
        md += `\`${mediaType}\`\n\n`;
        if (content.example) {
          md += '```json\n' + JSON.stringify(content.example, null, 2) + '\n```\n\n';
        }
      }
    }

    // Responses
    if (endpoint.responses.length > 0) {
      md += `**Responses:**\n\n`;
      for (const response of endpoint.responses) {
        md += `- **${response.statusCode}**: ${response.description}\n`;
      }
      md += '\n';
    }

    md += '---\n\n';
  };

  // Render by tags
  for (const [tag, endpoints] of endpointsByTag) {
    md += `## ${tag}\n\n`;
    const tagDef = contract.tags?.find((t) => t.name === tag);
    if (tagDef?.description) md += `${tagDef.description}\n\n`;
    for (const endpoint of endpoints) {
      renderEndpoint(endpoint);
    }
  }

  // Render untagged
  if (untaggedEndpoints.length > 0) {
    if (endpointsByTag.size > 0) {
      md += `## Other Endpoints\n\n`;
    } else {
      md += `## Endpoints\n\n`;
    }
    for (const endpoint of untaggedEndpoints) {
      renderEndpoint(endpoint);
    }
  }

  // Schemas
  if (contract.schemas.length > 0) {
    md += `## Schemas\n\n`;
    for (const schema of contract.schemas) {
      md += `### ${schema.name}\n\n`;
      if (schema.description) md += `${schema.description}\n\n`;
      md += '```json\n' + JSON.stringify(convertSchema(schema.schema), null, 2) + '\n```\n\n';
    }
  }

  return md;
}
